/**
 * The milk delivery grid (BR-23, P3-TR5).
 *
 * Customers down, days across — the shape the round is actually walked in, and
 * the shape a partner checks: "did Rahim get milk on the 12th?" is a cell, not
 * a filter.
 *
 * One save for the whole day's column. A request per customer on a rural
 * connection is a dozen chances to fail with no way to tell which landed, and
 * a repeat submission corrects rather than adding.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { MilkBalancePanel } from './MilkBalancePanel.js';
import { MilkSaleForm } from './MilkSaleForm.js';
import { useDeliveryGrid, useSaveDeliveries } from './api.js';

/** The last seven days, oldest first — a week is what fits on a phone. */
function lastWeek(today: string): { from: string; to: string } {
  const end = new Date(`${today}T00:00:00.000Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 6);

  return { from: start.toISOString().slice(0, 10), to: today };
}

export function MilkDeliveryGrid(): JSX.Element {
  const { t } = useTranslation();

  const today = todayInDhaka();
  const range = lastWeek(today);

  const { data } = useDeliveryGrid(range.from, range.to);
  const save = useSaveDeliveries();
  const { user } = useAuth();

  /*
   * The round is a delivery and staff may write it; a sale is income, which
   * §1.4 makes a partner's job. The server enforces the same split — the
   * delivery route takes any signed-in user, the sale route takes admin or
   * partner only.
   */
  const canSell = user?.role === 'admin' || user?.role === 'partner';

  const [entries, setEntries] = useState<Record<number, string>>({});
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const filled = Object.entries(entries).filter(([, litres]) => litres.trim() !== '');

  async function handleSubmit(): Promise<void> {
    setErrorKey(null);

    try {
      await save.mutateAsync({
        date: today,
        lines: filled.map(([customerId, litres]) => ({ customerId: Number(customerId), litres })),
      });

      setSaved(true);
      setEntries({});
    } catch (error) {
      // The column stays filled: retyping a dozen customers is how a round
      // goes unrecorded (NFR-2).
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('milk.delivery.title')}</h1>

      {/* Read-only and open to everyone: a shortfall is exactly the kind of
          figure §1.4 says every partner may see, and the person best placed to
          explain it is usually whoever walked the round. */}
      <MilkBalancePanel from={range.from} to={range.to} />

      {canSell && <MilkSaleForm />}

      {/* Today's column, as a form. The week behind it is read-only history. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        noValidate
      >
        <ul className="space-y-2">
          {data?.customers.map((customer) => (
            <li key={customer.id} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-800">{customer.name}</p>
                {customer.defaultRate !== null && (
                  <p className="text-xs text-gray-500">{t('milk.delivery.rate', { rate: customer.defaultRate })}</p>
                )}
              </div>

              <input
                aria-label={t('milk.delivery.litresFor', { name: customer.name })}
                inputMode="decimal"
                placeholder={data.cells[`${String(customer.id)}:${today}`] ?? '0'}
                value={entries[customer.id] ?? ''}
                onChange={(event) => setEntries((current) => ({ ...current, [customer.id]: event.target.value }))}
                className="min-h-touch w-20 rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
              />
            </li>
          ))}
        </ul>

        {data?.customers.length === 0 && (
          <p className="py-8 text-center text-gray-500">{t('milk.delivery.noCustomers')}</p>
        )}

        {errorKey !== null && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            {t(errorKey)}
          </p>
        )}

        {saved && errorKey === null && (
          <p role="status" className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
            {t('milk.delivery.saved')}
          </p>
        )}

        <button
          type="submit"
          disabled={filled.length === 0 || save.isPending}
          className="mt-4 min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
        >
          {t(save.isPending ? 'milk.delivery.saving' : 'milk.delivery.submit', { count: filled.length })}
        </button>
      </form>

      {/* The week behind, so a partner can see a customer who was missed. */}
      {data && data.days.length > 0 && (
        <section className="mt-6 overflow-x-auto" aria-label={t('milk.delivery.week')}>
          <h2 className="mb-2 text-sm font-medium text-gray-700">{t('milk.delivery.week')}</h2>
          <table className="min-w-max text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left font-medium text-gray-500">{t('milk.delivery.customer')}</th>
                {data.days.map((day) => (
                  <th key={day} className="px-2 py-1 text-right font-medium text-gray-500">
                    {day.slice(8)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.customers.map((customer) => (
                <tr key={customer.id} className="border-t border-gray-100">
                  <td className="whitespace-nowrap px-2 py-1 text-gray-800">{customer.name}</td>
                  {data.days.map((day) => (
                    <td key={day} className="px-2 py-1 text-right tabular-nums text-gray-600">
                      {data.cells[`${String(customer.id)}:${day}`] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
